import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, Subject } from 'rxjs';
import { catchError, concatMap, first, switchMap, take, tap } from 'rxjs/operators';

import { ENVIRONMENT, Environment } from '@environment';
import { AuthResponse, AuthService } from '@usheru-hff/authentication/domain';
import { ApiResponse } from '@usheru-hff/shared/domain';

type PendingStatus = 'pending' | 'completed';

// list of exceptions for not caching
const getExceptions = [
  'player/available',
  'player/purchased',
  'movie-actions/list',
  'movie-actions/follow-me/users/',
  'movie-actions/search/user/',
  '/user-actions/profile/'
];
@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);

  /**
   * Cache to store the responses of the requests
   *
   * If the item is a Subject it means that the request is pending
   */
  private cache = new Map<string, HttpResponse<unknown>>();
  private pendingCache = new Map<string, BehaviorSubject<PendingStatus>>();

  private isWaitingRefreshToken = false;
  /**
   * Emits a value when the token is refreshed
   */
  private refresedTokenListener = new Subject<string>();

  constructor(@Inject(ENVIRONMENT) private env: Environment) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    /**
     * If a value can not be cached it will be stored in the {@link cache} for the pendingListener
     * but the initial get to know if is cached will return null.
     * Like that we mantein this outside the case "CACHED - NOT PENDING" for the exception requests
     */
    let canBeCached = true;

    if (this.env.fakeApiResponse) {
      const fakeRes = this.processFakeApiResponse(request, this.env.fakeApiResponse);
      if (fakeRes) return fakeRes;
    }

    // Set values for the request that should not be cached
    if (this.isRequestACacheException(request)) {
      canBeCached = false;
      request = request.clone({ headers: request.headers.set('Cache-Control', 'no-cache') });
      return next.handle(request);
    }

    // a simpler approach would be to just use HttpRequest.urlWithParameters,
    // but it would not detect the same request having params ordered differently
    const urlWithSortedParams = this.urlWithSortedParams(request);

    // return this.processStandarRequest({ request, next, urlWithSortedParams });

    // ACCESS TOKEN REQUEST - CASE
    if (this.hasAccessTokenHeader(request)) {
      return this.processAccessTokenRequest({
        request,
        next,
        urlWithSortedParams,
        canBeCached
      });
      // STANDARD REQUEST - CASE
    } else {
      return this.processStandarRequest({ request, next, urlWithSortedParams, canBeCached });
    }
  }

  private isRequestACacheException(request: HttpRequest<unknown>): boolean {
    let exceptionFound = false;
    getExceptions.forEach(ex => {
      if (request.url.includes(ex)) {
        exceptionFound = true;
      }
    });
    return exceptionFound;
  }

  private urlWithSortedParams(request: HttpRequest<unknown>): string {
    const sortedParams = request.params
      .keys()
      .sort()
      .reduce((acc, key) => {
        let value = request.params.get(key);
        // Sort vañue if it is an array joining it with a comma
        const arr = value.split(',') || [];
        if (arr.length > 1) value = arr.sort().join(',');
        acc[key] = value;
        return acc;
      }, {});
    const params = new HttpParams({ fromObject: sortedParams });

    request = request.clone({ params });

    return request.urlWithParams;
  }

  private processStandarRequest(args: {
    request: HttpRequest<unknown>;
    next: HttpHandler;
    urlWithSortedParams: string;
    canBeCached: boolean;
  }): Observable<HttpEvent<unknown>> {
    const { request, next, urlWithSortedParams, canBeCached } = args;

    const cachedResponse = canBeCached ? this.cache.get(urlWithSortedParams) : null;
    const pendingListener = this.pendingCache.get(urlWithSortedParams);

    // NOT GET OR PUT continue with the request
    if (request.method !== 'GET' && request.method !== 'PUT') {
      return next.handle(request);
    }

    /**
     * PUT CASE - PUT request are used to update the cache values
     */
    if (request.method === 'PUT') {
      const pendingListener = this.pendingCache.get(urlWithSortedParams);
      if (!pendingListener) {
        this.pendingCache.set(urlWithSortedParams, new BehaviorSubject<PendingStatus>('pending'));
      } else {
        pendingListener.next('pending');
      }

      return next.handle(request)?.pipe(
        tap(event => {
          if (event instanceof HttpResponse) {
            const pendingListener = this.pendingCache.get(urlWithSortedParams);
            if (pendingListener) {
              pendingListener.next('completed');
            }
            this.cache.set(urlWithSortedParams, event);
          }
        })
      );
      // NOT CACHED - NOT PENDING - CASE
      // Initiate the pending listener and the request.
    } else if (!cachedResponse && !pendingListener) {
      this.pendingCache.set(urlWithSortedParams, new BehaviorSubject<PendingStatus>('pending'));
      return next.handle(request)?.pipe(
        tap(event => {
          if (event instanceof HttpResponse) {
            const pendingListener = this.pendingCache.get(urlWithSortedParams);
            this.cache.set(urlWithSortedParams, event);
            if (pendingListener) pendingListener.next('completed');
          }
        })
      );
      // NOT CACHED - PENDING - CASE
      // Wait for the pending request to complete and return the cached response
      // Used too to wait for the token refresh to be able to return the response
    } else if (!cachedResponse && pendingListener.value === 'pending') {
      return pendingListener?.pipe(
        first(v => v === 'completed'),
        switchMap(() => {
          const pendingResponse = this.cache.get(urlWithSortedParams);
          return of(pendingResponse as HttpResponse<unknown>);
        })
      );
      // CACHED - NOT PENDING - CASE
      // Return the cached response
    } else {
      return of(cachedResponse as HttpResponse<unknown>);
    }
  }

  /**
   *
   */
  private processAccessTokenRequest(args: {
    request: HttpRequest<unknown>;
    next: HttpHandler;
    urlWithSortedParams: string;
    canBeCached: boolean;
  }): Observable<HttpEvent<unknown>> {
    const { request } = args;

    /**
     * If the refresh token is being refreshed, we wait for the new token to be available
     */
    if (this.isWaitingRefreshToken) {
      return new Observable<HttpEvent<unknown>>(subscriber => {
        this.refresedTokenListener
          .asObservable()
          .pipe(take(1))
          .subscribe({
            next: jwt => {
              args.request = this.updateRequestHeaderToken(request, jwt);
              this.processStandarRequest(args).subscribe(subscriber);
            }
          });
      });
    }

    const status = this.authService.checkTokenStatus();
    // If the token available, we can proceed with the request
    if (status === 'available-token') {
      return this.processStandarRequest(args);
      // The request can proceed normally but can refresh the token after that
    } else if (status === 'close-expire-token') {
      return this.processStandarRequest(args)?.pipe(
        tap(() => {
          if (this.isWaitingRefreshToken) return;
          this.isWaitingRefreshToken = true;
          this.authService
            .refreshApi()
            .pipe(take(1))
            .subscribe({
              next: (token: AuthResponse) => {
                if (token) {
                  token = this.authService.setToken(token);
                  this.refresedTokenListener.next(token.accessToken);
                }
                this.isWaitingRefreshToken = false;
              },
              error: () => {
                this.authService.logout();
              }
            });
        })
      );
      // The access token and refresh token is expired, the request is not processed
    } else if (status === 'expired-refresh-token') {
      return of(null);
    }

    // status === 'expired-token'
    // Case when ACCESS TOKEN is not available BUT REFRESH TOKEN is available

    this.isWaitingRefreshToken = true;

    const refreshToken$ = this.authService.refreshApi()?.pipe(
      take(1),
      catchError(() => {
        this.authService.logout();
        return of(null);
      })
    );

    return refreshToken$?.pipe(
      concatMap(auth => {
        this.isWaitingRefreshToken = false;
        if (!auth) {
          console.error('Auth token empty');
          return;
        }

        this.authService.setToken(auth);
        const accessToken = auth.accessToken;
        this.refresedTokenListener.next(accessToken);
        args.request = this.updateRequestHeaderToken(request, accessToken);

        return this.processStandarRequest(args);
      })
    );
  }

  private processFakeApiResponse(
    request: HttpRequest<unknown>,
    apiRes: ApiResponse[]
  ): Observable<HttpResponse<unknown>> {
    let response;
    apiRes.forEach(fRes => {
      if (request.method == fRes.method && request.url.includes(fRes.contains)) {
        response = of(new HttpResponse({ status: fRes.status, body: fRes.body }));
      }
    });
    return response;
  }

  private updateRequestHeaderToken(request: HttpRequest<unknown>, newToken: string) {
    return request.clone({ headers: request.headers.set('Authorization', `Bearer ${newToken}`) });
  }

  private hasAccessTokenHeader(request: HttpRequest<unknown>): boolean {
    // SKIPS the refresh token request
    if (request.url.includes('token/refresh/')) return false;
    return request.headers.has('Authorization') && request.headers.get('Authorization')?.includes('Bearer');
  }
}
